import { Account, Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { delay, timeout } from '@ynap/promise-utils'
import { SET_SHIPPING_ADDRESS_REQUEST, SET_SHIPPING_ADDRESS_SUCCESS, SET_SHIPPING_ADDRESS_ERROR } from '../constants/ActionTypes'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { getCurrentShippingAddress } from '../selectors/getShippingAddressStep'
import { getPackagingOptions, getCurrentPackagingOption } from '../selectors/getPackagingOptionsStep'
import setShippingMethod from './setShippingMethod'
import { checkRectificationOnSuccess, checkRectificationOnError } from '../actions/cartRectification'
import { checkGuestIdentityError } from './checkGuestIdentityError'
import { getLocale, getShippingOptions, getCurrentShippingOption } from '@ynap/cart-state'
import { trimValues } from '@ynap/form'
import { WcsErrorResponse } from 'app/utils/api-utils'
import { isShippingAddressValid } from '../utils/addressValidationUtils'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'
import { setCheckoutNotification } from './checkoutNotification'

const EditableAddressFieldsList = [
    'addressId',
    'addressLine',
    'city',
    'country',
    'countryName',
    'firstName',
    'lastName',
    'personTitle',
    'phone1',
    'state',
    'stateName',
    'zipCode',
    'PCCC',
]

function wrapFetchWithJSON(fetch, ms) {
    return (input, init) =>
        timeout(fetch(input, init), ms).then((response) => {
            return response.json().then((json) => {
                if (!response.ok) {
                    if (Array.isArray(json.errors)) {
                        throw new WcsErrorResponse(response, json.errors)
                    } else {
                        throw new ResponseError(response)
                    }
                }
                return json
            })
        })
}

function setShippingAddressRequest() {
    return {
        type: SET_SHIPPING_ADDRESS_REQUEST,
    }
}

function setShippingAddressSuccess(data) {
    return {
        type: SET_SHIPPING_ADDRESS_SUCCESS,
        payload: data,
    }
}

function setShippingAddressError(error) {
    return {
        type: SET_SHIPPING_ADDRESS_ERROR,
        error,
    }
}

export default function setShippingAddress(values, redirect, packagingPath, paymentPath) {
    return async (dispatch, getState, fetch) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const currentShippingAddress = getCurrentShippingAddress(state)
        const fetchJSON = wrapFetchWithJSON(fetch, getTimeout(state))
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }
        dispatch(setShippingAddressRequest())
        // TODO: have the user sumary saved in state sometime earlier in checkout flow.
        const user = new Promise((resolve) => resolve(window.SF && window.SF.getUserSummary()))
        // This change is required so that registered customer is able to set transient (temporary) address. That is not saved into address book
        const isTransient = user.then((user) => (typeof values?.isTransient !== 'undefined' ? values.isTransient : !user))
        const promise = isTransient.then((isTransient) =>
            reconcileAddress(
                fetchJSON,
                trimValues(values),
                values?.isTransient || isTransient,
                values?.externalAddressValidation,
                currentShippingAddress,
                watsonConfig
            ).then(({ addressId }) => fetchJSON(Cart.setShippingAddress({ addressId, ...watsonConfig, langId, additionalHeaders })))
        )
        //TODO: purchase-path actions are fairly similar. Can probably be reviewed and abstracted.
        //tickets raised in https://jira.nap/browse/ISCC-348
        try {
            const data = await delay(promise, getDelay(state))
            const { shoppingCart: { orderItem } = {} } = data
            await isShippingAddressValid({ ...state, cart: data.shoppingCart }, dispatch)

            dispatch(setShippingAddressSuccess(data))
            const newState = { cart: data?.shoppingCart, locale: state?.locale }

            const [shippingOptions, jitShippingOptions] = getShippingOptions(newState, {})
            const [currentShippingOption] = getCurrentShippingOption(newState, {})

            const packagingOptions = getPackagingOptions(newState, {})
            const currentPackagingOption = getCurrentPackagingOption(newState, {})

            const redirectPath = getRedirectPath(
                shippingOptions,
                jitShippingOptions,
                currentShippingOption,
                packagingOptions,
                currentPackagingOption,
                redirect,
                packagingPath,
                paymentPath
            )

            checkRectificationOnSuccess(dispatch, data, redirectPath)

            return {
                ...data,
                addressId: orderItem && orderItem[0] && orderItem[0].addressId,
            }
        } catch (error) {
            dispatch(setShippingAddressError(error))
            checkRectificationOnError(error, dispatch)
            checkGuestIdentityError(error, dispatch, state)
            throw error
        }
    }
}

// Creates new, updates existing or uses selected address as is
// overwrites currentValues with values
export function reconcileAddress(fetch, values = {}, isTransient = false, externalAddressValidation = 'true', currentValues = {}, options = {}) {
    // addresses should never send email1
    if ('email1' in values) {
        delete values.email1
    }

    if (values.addressId) {
        // Use existing address (nothing changed or different address selected)
        if (
            !currentValues.addressId ||
            (currentValues.addressId && currentValues.addressId !== values.addressId) ||
            hasIdenticalValues(values, currentValues)
        ) {
            return Promise.resolve({ ...currentValues, ...values })
        }

        // Update existing address if not transient
        if (!values.transient) {
            return fetch(Account.updateAddress({ address: values, addressId: values.addressId, ...options })).then(({ addressId }) => ({
                ...currentValues,
                ...values,
                addressId,
                externalAddressValidation,
            })) // Inject address details in case WCS doesn't return them
        }
    }
    // Create new address
    return fetch(Account.addAddress({ address: { ...values, addressType: 'ShippingAndBilling', isTransient, externalAddressValidation }, ...options })).then(
        ({ addressId }) => ({
            ...values,
            addressId,
        })
    ) // WCS add address endpoint doesn't return address details so inject them in here
}

// Ensure all values are identical to existing address (It's fine if there are extra fields in the existing address)
// We don't care about addressType, since it's often defined in values but undefined in existing address and not an editable field anyway.
export function hasIdenticalValues(values = {}, currentValues = {}) {
    return !Object.keys(values)
        .filter((key) => EditableAddressFieldsList.includes(key))
        .find((key) => JSON.stringify(values[key]) !== JSON.stringify(currentValues[key]))
}

// Logic to redirect/skip shipping option and/or packaging options step when there is only one option available
export function getRedirectPath(
    shippingOptions,
    jitShippingOptions,
    currentShippingOption,
    packagingOptions,
    currentPackagingOption,
    shippingPath,
    packagingPath,
    paymentPath
) {
    let redirectPath = shippingPath

    // if there is only 1 shipping option and if current shipping option is not defined yet
    if (shippingOptions?.length === 1 && !currentShippingOption && jitShippingOptions?.length <= 1) {
        const option = shippingOptions[0]
        const values = {
            nominatedSlot: option?.defaultNominatedSlot,
            shippingOptionId: option?.id,
            shippingOptionIdJit: jitShippingOptions[0]?.id,
            signatureRequired: option?.defaultSignatureRequired,
        }

        setShippingMethod(values, packagingPath, 'Y', false, [], [], paymentPath)

        redirectPath = getPackagingOptionRedirect(packagingOptions, currentPackagingOption, packagingPath, paymentPath)

        // if there is only 1 shipping option and if current shipping option is already defined
    } else if (shippingOptions?.length === 1 && currentShippingOption && jitShippingOptions?.length <= 1) {
        redirectPath = getPackagingOptionRedirect(packagingOptions, currentPackagingOption, packagingPath, paymentPath)
    }

    return redirectPath
}

export function getPackagingOptionRedirect(packagingOptions, currentPackagingOption, packagingPath, paymentPath) {
    let redirectPath = packagingPath

    if ((packagingOptions?.length === 1 && currentPackagingOption) || !packagingOptions || !packagingOptions?.length) {
        redirectPath = paymentPath
    }

    return redirectPath
}
